import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import TrafficLights from "../TrafficLights";
import WindowFrame from "../WindowFrame";
import useGenie from "../../hooks/useGenie";
import ProjectPanel from "./ProjectPanel";
import { CodeView, ImageView, Message } from "./Editor";
import ShellLines from "../ShellLines";
import useShell from "../../hooks/useShell";
import { PROJECT, PROJECT_CWD, loadTree } from "../../utils/project";
import { DevServer, Prompt, TERMINALS } from "./Terminal";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  AssistantIcon,
  BoltIcon,
  BranchIcon,
  CheckIcon,
  DashIcon,
  DebugIcon,
  FileTreeIcon,
  IMAGE,
  ListTreeIcon,
  MaximizeIcon,
  PlusIcon,
  PredictIcon,
  SearchIcon,
  SplitIcon,
  TerminalIcon,
  UsersIcon,
  WorktreeIcon,
} from "./icons";

// Zed's own fonts, .ZedSans (IBM Plex Sans) and .ZedMono (Lilex), loaded when Zed first opens.
const FONT =
  "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,400;0,500;1,400&family=Lilex:ital,wght@0,400;0,700;1,400&display=swap";
const loadFont = () => {
  if (document.getElementById("zed-font")) return;
  const link = document.createElement("link");
  link.id = "zed-font";
  link.rel = "stylesheet";
  link.href = FONT;
  document.head.appendChild(link);
};

const ROOT = PROJECT;
const CHANGES = "Uncommitted Changes";
// It opens as Sylvester's Zed was: these tabs (the last one a preview, in italics), with its
// folders open down to it.
const START_TABS = [
  { key: "src/hooks/useClock.js" },
  { key: CHANGES },
  { key: "src/utils/shell.js" },
  { key: "src/assets/icons/phone/p2-16.webp" },
  { key: "package.json" },
  { key: "src/assets/icons/chrome/ext-5.png", preview: true },
];
const START_ACTIVE = "src/assets/icons/chrome/ext-5.png";
// Back goes through the tabs in the order they were opened.
const START_HISTORY = { stack: START_TABS.map((t) => t.key), at: START_TABS.length - 1 };
const START_OPEN = [
  "",
  "/src",
  "/src/assets",
  "/src/assets/icons",
  "/src/assets/icons/chrome",
  "/src/assets/icons/phone",
];

// The terminal panel's tabs: the dev server, then bash, which is open.
const START_TERMINALS = ["node", "bash"];

const ZOOMS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 8];

// Default frame: up to 1180 x 740, centered.
const zedFrame = (ws) => {
  const w = Math.min(1180, ws.w - 80);
  const h = Math.min(740, ws.h - 32);
  return { x: (ws.w - w) / 2, y: (ws.h - h) / 2, w, h };
};

const nameOf = (key) => key.split("/").pop();

// The tree's entry for a file, by its path in the repository.
const findNode = (tree, path) =>
  tree &&
  path.split("/").reduce((node, name) => node && node.children && node.children.find((c) => c.name === name), tree);

const CloseGlyph = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" />
  </svg>
);

const ChevronGlyph = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true">
    <path d="M4.5 6.5 8 10l3.5-3.5" />
  </svg>
);

// Zed, like Sylvester's: Gruvbox Light Hard, the project panel, tabs over the editor, and the
// terminal panel under it. The project is this site: its real files, read-only.
const ZedWindow = ({ workspace, open, front, onFocus, onClose }) => {
  const [exit, setExit] = useState("closed");
  const genie = useGenie({ app: "Zed", open, exit });
  const [maximized, setMaximized] = useState(false);
  const [tree, setTree] = useState(null);
  const [openFolders, setOpenFolders] = useState(() => new Set(START_OPEN.map((p) => ROOT + p)));
  const [tabs, setTabs] = useState(START_TABS);
  const [active, setActive] = useState(START_ACTIVE);
  const [history, setHistory] = useState(START_HISTORY);
  const [zoom, setZoom] = useState({ level: 1, fit: false });
  const [panel, setPanel] = useState(true);
  const [terminal, setTerminal] = useState(true);
  const [terminals, setTerminals] = useState(START_TERMINALS);
  const [terminalTab, setTerminalTab] = useState("bash");
  const [terminalHeight, setTerminalHeight] = useState(null); // px, once dragged
  // Whether the terminal has the keyboard: it does until you click elsewhere in the window.
  const [terminalFocus, setTerminalFocus] = useState(true);
  const main = useRef(null);
  const tabList = useRef(null);
  const showFile = useRef(null);

  useEffect(() => {
    if (!open) return;
    loadFont();
    if (!tree) loadTree().then(setTree, () => setTree({ name: ROOT, dir: true, children: [] }));
  }, [open, tree]);

  // bash, running the shell Ghostty runs, in the project. `exit` closes its tab.
  const shell = useShell({
    open: open && terminals.includes("bash"),
    active: open && front && !genie.hidden && terminal && terminalTab === "bash" && terminalFocus,
    shell: "bash",
    start: PROJECT_CWD,
    onExit: () => {
      setTerminals((ts) => ts.filter((t) => t !== "bash"));
      setTerminalTab("node");
      shell.reset();
    },
  });

  // The terminal's + opens bash again, once it's been closed.
  const newTerminal = () => {
    setTerminals((ts) => (ts.includes("bash") ? ts : [...ts, "bash"]));
    setTerminalTab("bash");
    setTerminalFocus(true);
  };

  // `open` in a terminal (here or Ghostty's) shows a project file in the editor, kept open, with
  // its folders open in the project panel.
  showFile.current = (path) => {
    const parts = path.split("/");
    setOpenFolders((folders) => {
      const next = new Set(folders);
      parts.slice(0, -1).forEach((_, i) => next.add([ROOT, ...parts.slice(0, i + 1)].join("/")));
      return next;
    });
    openFile(`${ROOT}/${path}`, false);
    setTerminalFocus(false);
  };

  useEffect(() => {
    const onOpen = (e) => e.detail && e.detail.file && showFile.current(e.detail.file);
    window.addEventListener("open-zed", onOpen);
    return () => window.removeEventListener("open-zed", onOpen);
  }, []);

  // The active tab scrolls into view.
  useLayoutEffect(() => {
    const list = tabList.current;
    const tab = list && list.querySelector(".zd-tab.is-active");
    if (!tab) return;
    if (tab.offsetLeft < list.scrollLeft) list.scrollLeft = tab.offsetLeft;
    else if (tab.offsetLeft + tab.offsetWidth > list.scrollLeft + list.clientWidth)
      list.scrollLeft = tab.offsetLeft + tab.offsetWidth - list.clientWidth;
  }, [active, tabs, open]);

  const activate = (key, record = true) => {
    setActive(key);
    setZoom({ level: 1, fit: false });
    if (record)
      setHistory((h) => (h.stack[h.at] === key ? h : { stack: [...h.stack.slice(0, h.at + 1), key], at: h.at + 1 }));
  };

  // Opens a file: as a preview (replacing the last preview, in italics) or kept open.
  const openFile = (treePath, preview) => {
    const key = treePath.slice(ROOT.length + 1);
    setTabs((ts) => {
      const at = ts.findIndex((t) => t.key === key);
      if (at >= 0) return preview ? ts : ts.map((t) => (t.key === key ? { key } : t));
      const previewAt = ts.findIndex((t) => t.preview);
      if (preview && previewAt >= 0) return ts.map((t, i) => (i === previewAt ? { key, preview: true } : t));
      const after =
        Math.max(
          0,
          ts.findIndex((t) => t.key === active),
        ) + 1;
      return [...ts.slice(0, after), { key, preview }, ...ts.slice(after)];
    });
    activate(key);
  };

  const closeTab = (key) => {
    const at = tabs.findIndex((t) => t.key === key);
    const rest = tabs.filter((t) => t.key !== key);
    setTabs(rest);
    if (key === active) {
      const next = rest[Math.min(at, rest.length - 1)];
      setActive(next ? next.key : null);
    }
  };

  const step = (delta) => {
    const at = history.at + delta;
    const key = history.stack[at];
    if (key === undefined) return;
    setHistory((h) => ({ ...h, at }));
    if (!tabs.some((t) => t.key === key)) setTabs((ts) => [...ts, { key }]);
    activate(key, false);
  };

  const toggleFolder = (path) =>
    setOpenFolders((s) => {
      const next = new Set(s);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const zoomBy = (direction) =>
    setZoom(({ level }) => {
      const i = ZOOMS.findIndex((z) => z >= level - 0.001);
      return { level: ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, i + direction))], fit: false };
    });

  // Dragging the line above the terminal resizes it.
  const resizeTerminal = (e) => {
    e.preventDefault();
    const box = main.current.getBoundingClientRect();
    const move = (ev) => setTerminalHeight(Math.max(90, Math.min(box.height - 150, box.bottom - ev.clientY)));
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const hide = (how) => {
    setExit(how);
    if (how === "closed") {
      setMaximized(false);
      setTabs(START_TABS);
      setActive(START_ACTIVE);
      setHistory(START_HISTORY);
      setOpenFolders(new Set(START_OPEN.map((p) => ROOT + p)));
      setTerminals(START_TERMINALS);
      setTerminalTab("bash");
      setTerminalFocus(true);
      shell.reset();
    }
    onClose();
  };

  const node = active && active !== CHANGES ? findNode(tree, active) : null;
  const isImage = active && IMAGE.test(active);
  let content;
  if (!active) content = <Message>No open files</Message>;
  else if (active === CHANGES) content = <Message>No uncommitted changes</Message>;
  else if (!tree) content = null;
  else if (!node) content = <Message>This file isn't in the project.</Message>;
  else if (node.binary) content = <Message>This file is binary and can't be shown.</Message>;
  else if (isImage) content = <ImageView path={active} zoom={zoom.level} fit={zoom.fit} />;
  else content = <CodeView key={active} path={active} />;

  const state = open ? "nw--open" : `nw--${exit}`;
  return (
    <WindowFrame
      workspace={workspace}
      initial={zedFrame}
      min={{ w: 720, h: 440 }}
      titlebar={40}
      open={open}
      front={front}
      zoomed={maximized}
      onFocus={onFocus}
      onToggleZoom={() => setMaximized((m) => !m)}
    >
      <div
        role="dialog"
        aria-label="Zed"
        aria-hidden={!open}
        ref={genie.ref}
        onPointerDown={(e) => setTerminalFocus(Boolean(e.target.closest(".zd-terminal")))}
        className={`nw zd ${state} ${maximized ? "nw--max" : ""} ${front ? "is-front" : ""} ${
          genie.hidden ? "is-genie-hidden" : ""
        }`}
      >
        <div className="traffic">
          <TrafficLights
            onClose={() => hide("closed")}
            onMinimize={() => genie.minimize(() => hide("minimized"))}
            onPrepare={genie.prepare}
            onZoom={() => setMaximized(!maximized)}
            zoomed={maximized}
          />
        </div>

        <header className="zd-titlebar no-select">
          <span className="zd-project-name">{ROOT}</span>
          <span className="zd-title-item">
            <WorktreeIcon aria-hidden="true" />
            main
          </span>
          <span className="zd-title-slash">/</span>
          <span className="zd-title-item">
            <BranchIcon aria-hidden="true" />
            master
          </span>
          <span className="zd-signin" aria-hidden="true">
            Sign In
            <ChevronGlyph />
          </span>
        </header>

        <div className="zd-body">
          {panel && (
            <ProjectPanel
              tree={tree}
              open={openFolders}
              selected={active && active !== CHANGES ? `${ROOT}/${active}` : null}
              onToggle={toggleFolder}
              onOpen={openFile}
            />
          )}

          <div className="zd-main" ref={main}>
            <section className="zd-pane" aria-label="Editor">
              <div className="zd-tabbar no-select">
                <div className="zd-nav">
                  <button type="button" onClick={() => step(-1)} disabled={history.at === 0} aria-label="Go back">
                    <ArrowLeftIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => step(1)}
                    disabled={history.at === history.stack.length - 1}
                    aria-label="Go forward"
                  >
                    <ArrowRightIcon />
                  </button>
                </div>
                <div className="zd-tabs" role="tablist" ref={tabList}>
                  {tabs.map((tab) => (
                    <div
                      key={tab.key}
                      role="tab"
                      aria-selected={tab.key === active}
                      className={`zd-tab ${tab.key === active ? "is-active" : ""} ${tab.preview ? "is-preview" : ""} ${
                        tab.key === CHANGES || IMAGE.test(tab.key) ? "is-muted" : ""
                      }`}
                      onClick={() => activate(tab.key)}
                      onDoubleClick={() => setTabs((ts) => ts.map((t) => (t.key === tab.key ? { key: t.key } : t)))}
                      title={tab.key}
                    >
                      {tab.key === CHANGES && <BranchIcon className="zd-tab-icon" aria-hidden="true" />}
                      <span className="zd-tab-title">{nameOf(tab.key)}</span>
                      <button
                        type="button"
                        className="zd-tab-close"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeTab(tab.key);
                        }}
                        aria-label={`Close ${nameOf(tab.key)}`}
                      >
                        <CloseGlyph />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="zd-toolbar no-select">
                {active && active !== CHANGES && <span className="zd-breadcrumb">{active}</span>}
                {isImage && node && !node.binary && (
                  <span className="zd-zoom">
                    <button type="button" onClick={() => zoomBy(-1)} aria-label="Zoom out">
                      <DashIcon />
                    </button>
                    <span className="zd-zoom-level">{zoom.fit ? "Fit" : `${Math.round(zoom.level * 100)}%`}</span>
                    <button type="button" onClick={() => zoomBy(1)} aria-label="Zoom in">
                      <PlusIcon />
                    </button>
                    <button
                      type="button"
                      onClick={() => setZoom((z) => ({ ...z, fit: !z.fit }))}
                      aria-label="Fit to view"
                      aria-pressed={zoom.fit}
                    >
                      <MaximizeIcon />
                    </button>
                  </span>
                )}
              </div>

              <div className="zd-content">{content}</div>
            </section>

            {terminal && (
              <>
                <div
                  className="zd-splitter"
                  onPointerDown={resizeTerminal}
                  role="separator"
                  aria-orientation="horizontal"
                />
                <section
                  className="zd-terminal"
                  aria-label="Terminal"
                  style={terminalHeight ? { flexBasis: terminalHeight } : undefined}
                >
                  <div className="zd-terminal-tabs no-select">
                    <div className="zd-terminal-tablist" role="tablist">
                      {terminals.map((id) => (
                        <button
                          type="button"
                          key={id}
                          role="tab"
                          aria-selected={id === terminalTab}
                          className={`zd-terminal-tab ${id === terminalTab ? "is-active" : ""}`}
                          onClick={() => setTerminalTab(id)}
                        >
                          <TerminalIcon aria-hidden="true" />
                          {TERMINALS[id]}
                        </button>
                      ))}
                    </div>
                    <span className="zd-terminal-tools">
                      <button type="button" onClick={newTerminal} aria-label="New terminal">
                        <PlusIcon />
                      </button>
                      <SplitIcon aria-hidden="true" />
                      <MaximizeIcon aria-hidden="true" />
                    </span>
                  </div>
                  <div
                    className="zd-terminal-body edit-cursor"
                    ref={shell.screenRef}
                    onMouseUp={terminalTab === "bash" ? shell.onMouseUp : undefined}
                    onScroll={shell.onScroll}
                  >
                    {terminalTab === "bash" ? (
                      <ShellLines shell={shell} Prompt={Prompt} prefix="zd" tty="ttys003" />
                    ) : (
                      <DevServer />
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        </div>

        <footer className="zd-status no-select">
          <button
            type="button"
            className={`zd-status-icon ${panel ? "is-on" : ""}`}
            onClick={() => setPanel((p) => !p)}
            aria-label="Project panel"
            aria-pressed={panel}
          >
            <FileTreeIcon />
          </button>
          <span className="zd-status-icon" aria-hidden="true">
            <BranchIcon />
          </span>
          <span className="zd-status-sep" />
          <span className="zd-status-icon" aria-hidden="true">
            <SearchIcon />
          </span>
          <span className="zd-status-icon" aria-hidden="true">
            <BoltIcon />
          </span>
          <span className="zd-status-icon" aria-hidden="true">
            <CheckIcon />
          </span>
          <span className="zd-status-spacer" />
          <span className="zd-status-icon zd-status-icon--wide" aria-hidden="true">
            <PredictIcon />
          </span>
          <span className="zd-status-sep" />
          <button
            type="button"
            className={`zd-status-icon ${terminal ? "is-on is-focused" : ""}`}
            onClick={() => setTerminal((t) => !t)}
            aria-label="Terminal panel"
            aria-pressed={terminal}
          >
            <TerminalIcon />
          </button>
          <span className="zd-status-icon" aria-hidden="true">
            <DebugIcon />
          </span>
          <span className="zd-status-sep" />
          <span className="zd-status-icon" aria-hidden="true">
            <ListTreeIcon />
          </span>
          <span className="zd-status-icon" aria-hidden="true">
            <UsersIcon />
          </span>
          <span className="zd-status-icon" aria-hidden="true">
            <AssistantIcon />
          </span>
          <span className="zd-status-sep" />
          <span className="zd-status-icon" aria-hidden="true">
            <SplitIcon />
          </span>
        </footer>
      </div>
    </WindowFrame>
  );
};

export default ZedWindow;
